import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "How accurate is the AI in handling real customer calls?",
    answer: "Our AI agent is trained on your specific business workflows, FAQs, and call patterns. We test it with real scenarios before going live. Typical accuracy for appointment booking, lead qualification, pricing queries, and booking inquiries exceeds 90%, because the agent is tuned exclusively for your business, not a generic model."
  },
  {
    question: "Can the AI handle both inbound and outbound calls?",
    answer: "Yes. It can answer incoming calls instantly and also make outbound calls for lead follow-ups, appointment reminders, missed-call recovery, reservation confirmations, and more. You choose which workflows you want activated."
  },
  {
    question: "Will the AI sound robotic or frustrate my customers?",
    answer: "No. We use natural, low-latency voice technology with real-time reasoning. The agent responds instantly, uses a calm professional tone, and follows your brand voice. More importantly, scripts are crafted for clarity, not gimmicks — so customers feel like they're speaking to a competent assistant, not a chatbot."
  },
  {
    question: "Can the AI escalate a call to a human if needed?",
    answer: "Absolutely. If the caller asks for a human, or the AI detects a scenario outside its allowed scope, it smoothly transfers the call to your team or logs a callback request. No loops, no frustration."
  },
  {
    question: "What do you need from us to get started?",
    answer: "We'll begin with a short virtual or in-person meeting to understand your use cases and how your support team currently operates. Then we review your existing materials—FAQs, scripts, workflows, tools you use, and any internal documentation. Based on this, we build and customize your AI calling agent so it can handle calls exactly the way your business needs. Once set up, it starts taking calls automatically with no technical effort required from your side."
  },
  {
    question: "How does pricing work?",
    answer: "There's a one-time setup fee to build and customize your agent, plus a monthly subscription based on usage (calls + workflows). We manage updates, improvements, and monitoring — you don't need an internal tech team."
  },
  {
    question: "Will it integrate with my existing tools (CRM, booking system, WhatsApp, etc.)?",
    answer: "Yes. We integrate with popular CRM and scheduling systems used by real estate, clinics, and hotels. If you use a custom system, we can connect through APIs or provide a lightweight integration layer."
  },
  {
    question: "What kind of results should I expect?",
    answer: "Most businesses see:\n• Faster lead response\n• Higher booking confirmations\n• Reduced missed calls\n• Lower staff load\n• Better customer experience during peak hours\n\nThe AI handles the repetitive calls so your team can focus on tasks that actually drive revenue."
  }
];

function FAQAccordion({ faq, index }: { faq: FAQItem; index: number }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-6 px-6 flex items-start justify-between text-left hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
      >
        <div className="flex items-start gap-4 flex-1">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
            <span className="text-emerald-600 font-semibold text-sm">{index + 1}</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</h3>
        </div>
        <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
          <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-xl text-emerald-600 transition-transform duration-200`}></i>
        </div>
      </button>
      {isOpen && (
        <div className="px-6 pb-6 pl-[72px]">
          <p className="text-gray-600 leading-relaxed whitespace-pre-line">{faq.answer}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const navigate = useNavigate();

  const goToContact = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate('/#re-contact');
    setTimeout(() => {
      const el = document.getElementById('re-contact');
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <i className="ri-question-line"></i>
            <span>Frequently Asked Questions</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Everything You Need to Know
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get answers to common questions about our AI calling agents and how they can transform your business communication.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {faqs.map((faq, index) => (
              <FAQAccordion key={index} faq={faq} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            We're here to help. Get in touch with our team for personalized answers.
          </p>
          <a 
            href="/#re-contact" 
            onClick={goToContact}
            className="inline-block bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Contact Us
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}